import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ideas as ideasApi, canon, Idea } from '../lib/api'
import SkeletonLoader from '../components/SkeletonLoader'

// --- Types ---

type ClarityLevel = 'EMPTY' | 'FORMING' | 'SOLID' | 'VALIDATED'
type IdeaCategory = 'all' | 'local' | 'shared' | 'featured' | 'canon'
type ViewMode = 'grid' | 'list'

interface IdeaGridItem {
  id: string
  title: string
  source: 'local' | 'canon' | 'shared' | 'featured'
  clarity: ClarityLevel
  archetype_id?: string
  has_cb: boolean
  has_sp: boolean
  has_bp: boolean
  updated_at?: string
  created_at?: string
  link?: string
  canon_repo?: string
  stakes?: string
  owner_email?: string
  shared_role?: string
  tagline?: string
}

// --- Helpers ---

function getClarityLevel(idea: Idea): ClarityLevel {
  const c = idea.canon || { CB: '', SP: '', BP: '' }
  const hasCB = !!(c.CB && c.CB.trim().length > 50)
  const hasSP = !!(c.SP && c.SP.trim().length > 50)
  const hasBP = !!(c.BP && c.BP.trim().length > 50)
  const filled = [hasCB, hasSP, hasBP].filter(Boolean).length
  if (filled === 0) return 'EMPTY'
  if (filled === 1) return 'FORMING'
  if (filled === 2) return 'SOLID'
  return 'VALIDATED'
}

const clarityConfig: Record<
  ClarityLevel,
  { label: string; barClass: string; textClass: string; fill: string }
> = {
  EMPTY: {
    label: 'Empty',
    barClass: 'bg-gray-600',
    textClass: 'text-gray-500',
    fill: '0%',
  },
  FORMING: {
    label: 'Forming',
    barClass: 'bg-yellow-500',
    textClass: 'text-yellow-400',
    fill: '33%',
  },
  SOLID: {
    label: 'Solid',
    barClass: 'bg-blue-500',
    textClass: 'text-blue-400',
    fill: '66%',
  },
  VALIDATED: {
    label: 'Validated',
    barClass: 'bg-green-500',
    textClass: 'text-green-400',
    fill: '100%',
  },
}

const categoryLabels: Record<IdeaCategory, string> = {
  all: 'All Ideas',
  local: 'My Ideas',
  shared: 'Shared',
  featured: 'Featured',
  canon: 'Canon Projects',
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

// --- Canon Doc Dots ---

function CanonDots({
  has_cb,
  has_sp,
  has_bp,
}: {
  has_cb: boolean
  has_sp: boolean
  has_bp: boolean
}) {
  return (
    <div className="flex items-center gap-1">
      {[
        { has: has_cb, label: 'CB' },
        { has: has_sp, label: 'SP' },
        { has: has_bp, label: 'BP' },
      ].map(({ has, label }) => (
        <div
          key={label}
          className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold transition ${
            has
              ? 'bg-emerald-900/40 text-emerald-400'
              : 'bg-gray-800 text-gray-600'
          }`}
          title={`${label}: ${has ? 'Present' : 'Missing'}`}
        >
          {label[0]}
        </div>
      ))}
    </div>
  )
}

// --- Source Badge ---

function SourceBadge({ source, stakes }: { source: string; stakes?: string }) {
  const badges: Record<
    string,
    { label: string; classes: string; icon: string }
  > = {
    featured: {
      label: 'FEATURED',
      classes: 'bg-amber-900/40 text-amber-300',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    },
    shared: {
      label: 'SHARED',
      classes: 'bg-teal-900/40 text-teal-300',
      icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
    },
    canon: {
      label: 'CANON',
      classes: 'bg-purple-900/40 text-purple-300',
      icon: 'M6 3v12M18 9a3 3 0 0 1-3 3H6',
    },
    local: {
      label: 'LOCAL',
      classes: 'bg-gray-800 text-gray-400',
      icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z',
    },
  }

  const badge = badges[source] || badges.local

  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.classes}`}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={badge.icon}
          />
        </svg>
        {badge.label}
      </span>
      {stakes === 'HIGH' && source !== 'featured' && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-900/30 text-amber-400">
          <svg
            className="w-2.5 h-2.5"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          HIGH
        </span>
      )}
    </div>
  )
}

// --- Idea Card ---

function IdeaCard({
  item,
  index,
}: {
  item: IdeaGridItem
  index: number
}) {
  const clarityInfo = clarityConfig[item.clarity]
  const isFeatured = item.source === 'featured'

  return (
    <Link
      to={item.link || `/ideas/${item.id}`}
      className={`block relative bg-gray-900 rounded-lg border p-5 group transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${
        isFeatured
          ? 'border-amber-700 ring-1 ring-amber-800/40'
          : 'border-gray-800 hover:border-gray-700'
      }`}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      {/* Featured ribbon */}
      {isFeatured && (
        <div className="absolute -top-px -right-px">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold px-2.5 py-0.5 rounded-bl-lg rounded-tr-lg shadow-sm">
            TRY ME
          </div>
        </div>
      )}

      {/* Top row: source badge + canon dots */}
      <div className="flex items-center justify-between mb-3">
        <SourceBadge source={item.source} stakes={item.stakes} />
        <CanonDots
          has_cb={item.has_cb}
          has_sp={item.has_sp}
          has_bp={item.has_bp}
        />
      </div>

      {/* Title */}
      <h3
        className={`text-base font-semibold mb-1 transition line-clamp-2 ${
          isFeatured
            ? 'text-amber-200 group-hover:text-amber-300'
            : 'text-gray-100 group-hover:text-brand-400'
        }`}
      >
        {item.title}
      </h3>

      {/* Tagline for featured */}
      {isFeatured && item.tagline && (
        <p className="text-xs text-amber-400/70 mb-2 italic">{item.tagline}</p>
      )}

      {/* Archetype tag */}
      {item.archetype_id && !isFeatured && (
        <div className="mb-3">
          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-400">
            {item.archetype_id.replace(/_/g, ' ')}
          </span>
        </div>
      )}

      {/* Clarity bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-gray-500">Clarity</span>
          <span className={`font-medium ${clarityInfo.textClass}`}>
            {clarityInfo.label}
          </span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-full rounded-full ${clarityInfo.barClass} transition-all duration-500`}
            style={{ width: clarityInfo.fill }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {item.updated_at
            ? formatDate(item.updated_at)
            : item.created_at
              ? formatDate(item.created_at)
              : ''}
        </span>
        {item.shared_role && (
          <span className="text-[10px] text-teal-400 capitalize">
            {item.shared_role}
          </span>
        )}
        <span
          className={`font-medium text-xs transition opacity-0 group-hover:opacity-100 ${
            isFeatured ? 'text-amber-400' : 'text-brand-400'
          }`}
        >
          Open →
        </span>
      </div>
    </Link>
  )
}

// --- Main Dashboard ---

export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [allIdeas, setAllIdeas] = useState<IdeaGridItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [category, setCategory] = useState<IdeaCategory>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [search, setSearch] = useState('')

  // Load ideas from all sources
  const loadIdeas = useCallback(async () => {
    try {
      setError(null)
      const items: IdeaGridItem[] = []

      // Load local ideas (user-created from DB)
      // Note: /api/ideas returns both paradigm + user ideas with shape {id, name, canon_files, phase}.
      // Paradigm ideas are also loaded via canon.getProjects() below, so we track IDs to avoid duplicates.
      const canonIdeaIds = new Set<string>()
      try {
        const localIdeas = await ideasApi.list()
        for (const idea of localIdeas) {
          // API returns {id, name, canon_files, phase} — use correct field names
          const raw = idea as unknown as { id: string; name: string; canon_files?: string[]; phase?: string }
          const id = raw.id || (idea as any).idea_id
          const title = raw.name || (idea as any).title || ''
          const canonFiles = raw.canon_files || []
          // Skip paradigm ideas here — they'll appear as CANON cards below
          if (canonFiles.length > 0) {
            canonIdeaIds.add(id)
            continue
          }
          const c = idea.canon || { CB: '', SP: '', BP: '' }
          items.push({
            id: id,
            title: title,
            source: 'local',
            clarity: getClarityLevel(idea),
            archetype_id: idea.archetype_id,
            has_cb: !!(c.CB && c.CB.trim().length > 50),
            has_sp: !!(c.SP && c.SP.trim().length > 50),
            has_bp: !!(c.BP && c.BP.trim().length > 50),
            updated_at: idea.updated_at,
            created_at: idea.created_at,
          })
        }
      } catch {
        // May fail if API not available — continue with other sources
      }

      // Load shared ideas
      try {
        const shared = await ideasApi.listShared()
        for (const s of shared) {
          items.push({
            id: s.idea_id,
            title: s.title,
            source: 'shared',
            clarity: 'FORMING',
            has_cb: false,
            has_sp: false,
            has_bp: false,
            owner_email: s.owner_email,
            shared_role: s.role,
            updated_at: s.updated_at,
          })
        }
      } catch {
        // Sharing may not be available
      }

      // Load featured ideas
      try {
        const featured = await canon.getFeatured()
        for (const f of featured) {
          const cbSpBp = [f.has_cb, f.has_sp, f.has_bp].filter(Boolean).length
          items.push({
            id: f.id || `featured-${f.title}`,
            title: f.title,
            source: 'featured',
            clarity: cbSpBp === 0 ? 'EMPTY' : cbSpBp === 1 ? 'FORMING' : cbSpBp === 2 ? 'SOLID' : 'VALIDATED',
            has_cb: f.has_cb ?? false,
            has_sp: f.has_sp ?? false,
            has_bp: f.has_bp ?? false,
            tagline: f.tagline,
            link: `/featured/${f.id}`,
          })
        }
      } catch {
        // Featured API may not be available
      }

      // Load canon projects (paradigm ideas from filesystem)
      try {
        const canonProjects = await canon.getProjects()
        for (const p of canonProjects) {
          const realId = p.id || p.title
          items.push({
            id: `canon-${realId}`,  // Prefixed for React key uniqueness
            title: p.title,
            source: 'canon',
            link: `/ideas/${realId}`,  // Route to the actual idea workspace
            clarity:
              [p.has_cb, p.has_sp, p.has_bp].filter(Boolean).length === 0
                ? 'EMPTY'
                : [p.has_cb, p.has_sp, p.has_bp].filter(Boolean).length === 1
                  ? 'FORMING'
                  : [p.has_cb, p.has_sp, p.has_bp].filter(Boolean).length === 2
                    ? 'SOLID'
                    : 'VALIDATED',
            has_cb: p.has_cb,
            has_sp: p.has_sp,
            has_bp: p.has_bp,
            stakes: p.stakes,
            canon_repo: p.canon_repo,
          })
        }
      } catch {
        // Canon API may not be available
      }

      setAllIdeas(items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ideas')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadIdeas()
  }, [loadIdeas])

  // Filter and search
  const filteredIdeas = useMemo(() => {
    let result = allIdeas

    // Category filter
    if (category !== 'all') {
      result = result.filter((item) => item.source === category)
    }

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          (item.archetype_id && item.archetype_id.toLowerCase().includes(q))
      )
    }

    return result
  }, [allIdeas, category, search])

  // Stats
  const stats = useMemo(() => {
    return {
      total: allIdeas.length,
      local: allIdeas.filter((i) => i.source === 'local').length,
      shared: allIdeas.filter((i) => i.source === 'shared').length,
      featured: allIdeas.filter((i) => i.source === 'featured').length,
      canon: allIdeas.filter((i) => i.source === 'canon').length,
    }
  }, [allIdeas])

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-7xl mx-auto w-full">
        <div className="mb-6">
          <div className="h-8 w-64 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded animate-pulse bg-[length:200%_100%]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <SkeletonLoader variant="card" count={8} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">
            Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {stats.total} idea{stats.total !== 1 ? 's' : ''} across your workspace
          </p>
        </div>
        <button
          onClick={() => navigate('/ideas/new')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-medium text-sm rounded-lg transition shadow-sm"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Idea
        </button>
      </div>

      {/* Controls: search + category filter + view toggle */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ideas..."
            className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition"
          />
        </div>

        {/* Category filter */}
        <div className="flex gap-1 overflow-x-auto">
          {(
            Object.entries(categoryLabels) as [IdeaCategory, string][]
          ).map(([key, label]) => {
            const count =
              key === 'all'
                ? stats.total
                : stats[key as keyof typeof stats] || 0
            return (
              <button
                key={key}
                onClick={() => setCategory(key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  category === key
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-900 text-gray-400 hover:text-gray-200 hover:bg-gray-800 border border-gray-800'
                }`}
              >
                {label}
                {count > 0 && (
                  <span className="ml-1.5 text-[10px] opacity-70">{count}</span>
                )}
              </button>
            )
          })}
        </div>

        {/* View toggle */}
        <div className="flex border border-gray-800 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 transition-colors ${
              viewMode === 'grid'
                ? 'bg-gray-800 text-gray-200'
                : 'text-gray-500 hover:text-gray-300'
            }`}
            title="Grid view"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm0 11h7v7h-7v-7zM3 14h7v7H3v-7z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 transition-colors ${
              viewMode === 'list'
                ? 'bg-gray-800 text-gray-200'
                : 'text-gray-500 hover:text-gray-300'
            }`}
            title="List view"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-950 border border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {filteredIdeas.length === 0 && !error && (
        <div className="text-center py-16">
          <svg
            className="w-16 h-16 text-gray-700 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          {search ? (
            <>
              <h3 className="text-lg font-semibold text-gray-300 mb-1">
                No ideas match "{search}"
              </h3>
              <p className="text-gray-500 text-sm">
                Try a different search or{' '}
                <button
                  onClick={() => setSearch('')}
                  className="text-brand-400 hover:text-brand-300"
                >
                  clear the filter
                </button>
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-300 mb-1">
                No ideas yet
              </h3>
              <p className="text-gray-500 text-sm mb-4">
                Start by creating your first idea
              </p>
              <button
                onClick={() => navigate('/ideas/new')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create your first idea
              </button>
            </>
          )}
        </div>
      )}

      {/* Idea Grid */}
      {filteredIdeas.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredIdeas.map((item, index) => (
            <IdeaCard key={item.id} item={item} index={index} />
          ))}
        </div>
      )}

      {/* Idea List */}
      {filteredIdeas.length > 0 && viewMode === 'list' && (
        <div className="space-y-2">
          {filteredIdeas.map((item) => (
            <Link
              key={item.id}
              to={item.link || `/ideas/${item.id}`}
              className="flex items-center gap-4 p-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-gray-700 transition group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <SourceBadge source={item.source} stakes={item.stakes} />
                  <h3 className="text-sm font-semibold text-gray-100 group-hover:text-brand-400 truncate">
                    {item.title}
                  </h3>
                </div>
                {item.archetype_id && (
                  <span className="text-[11px] text-gray-500">
                    {item.archetype_id.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <CanonDots
                  has_cb={item.has_cb}
                  has_sp={item.has_sp}
                  has_bp={item.has_bp}
                />
                <span
                  className={`text-xs font-medium ${clarityConfig[item.clarity].textClass}`}
                >
                  {clarityConfig[item.clarity].label}
                </span>
                <span className="text-xs text-gray-500">
                  {item.updated_at
                    ? formatDate(item.updated_at)
                    : item.created_at
                      ? formatDate(item.created_at)
                      : ''}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
